import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSwitch from "../LanguageSwitch/LanguageSwitch";
import logoGC from "../../assets/images/logogcnew.png";
import ChicPlainLogo from "../../assets/images/b2b/Chic_plain_logo_transparentBG.svg";
import B2BLogo from "../../assets/images/b2b/GCB_logo_square_transparentbg.png";
import StarFieldBackground from "../MainPageHero/StarFieldBackground";
import "./Header.css";
import { Facebook, Instagram, Menu } from "lucide-react";

function Header() {
  const { t } = useTranslation();
  const location = useLocation();
  const isB2BPage =
    location.pathname.startsWith("/b2b") || location.pathname.startsWith("/gcb");
  const isProfilePage =
    location.pathname.includes("/b2b/profile") ||
    location.pathname.includes("/gcb/profile");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [aboutDropdownOpen, setAboutDropdownOpen] = useState(false);
  const [membershipDropdownOpen, setMembershipDropdownOpen] = useState(false);
  const aboutDropdownRef = useRef(null);
  const membershipDropdownRef = useRef(null);
  const [scrollValues, setScrollValues] = useState({
    smallLogoPadding: 0,
    navOpacity: 0,
    shadowOpacity: 0,
  });

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    // Check immediately
    checkMobile();
    // Also check after a short delay to ensure it's set
    const timeoutId = setTimeout(checkMobile, 100);
    window.addEventListener('resize', checkMobile);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  const toggleMenu = () => {
    const newState = !isMenuOpen;
    setIsMenuOpen(newState);
    // Prevent body scroll when menu is open
    if (newState) {
      document.body.classList.add('menu-open');
    } else {
      document.body.classList.remove('menu-open');
    }
  };
  
  const closeMenu = () => {
    setIsMenuOpen(false);
    document.body.classList.remove('menu-open');
  };

  const toggleAboutDropdown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only toggle on desktop (width > 768px)
    if (window.innerWidth > 768) {
      setAboutDropdownOpen(!aboutDropdownOpen);
      setMembershipDropdownOpen(false);
    }
  };

  const toggleMembershipDropdown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only toggle on desktop (width > 768px)
    if (window.innerWidth > 768) {
      setMembershipDropdownOpen(!membershipDropdownOpen);
      setAboutDropdownOpen(false);
    }
  };

  const closeDropdowns = () => {
    setAboutDropdownOpen(false);
    setMembershipDropdownOpen(false);
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside dropdown wrappers using refs
      const isClickInsideAbout = aboutDropdownRef.current?.contains(event.target);
      const isClickInsideMembership = membershipDropdownRef.current?.contains(event.target);
      
      if (!isClickInsideAbout && !isClickInsideMembership) {
        if (aboutDropdownOpen || membershipDropdownOpen) {
          closeDropdowns();
        }
      }
    };
    
    if (aboutDropdownOpen || membershipDropdownOpen) {
      // Use a small delay to allow the click event that opened the dropdown to complete
      const timeoutId = setTimeout(() => {
        document.addEventListener('click', handleClickOutside, true);
      }, 10);
      
      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('click', handleClickOutside, true);
      };
    }
  }, [aboutDropdownOpen, membershipDropdownOpen]);

  useEffect(() => {
    // Constants for the animation
    const NAVBAR_HEIGHT = 60; // Height of the top navbar in pixels
    const BIG_LOGO_HEIGHT = 120; // Height of the big logo

    const handleScroll = () => {
      const scrollY = window.scrollY;

      // Calculate small logo padding (slides up from NAVBAR_HEIGHT to 0)
      // Logo slides up as user scrolls down
      const smallSpeed = 1; // You can adjust this for different animation speeds
      const ySmall = scrollY * smallSpeed;

      let smallPadding = NAVBAR_HEIGHT - ySmall;
      if (smallPadding > NAVBAR_HEIGHT) smallPadding = NAVBAR_HEIGHT;
      if (smallPadding < 0) smallPadding = 0;

      // Calculate navbar opacity (fades from white to dark as logo slides up)
      let navOpacity = ySmall / BIG_LOGO_HEIGHT;
      if (navOpacity > 1) navOpacity = 1;
      if (navOpacity < 0) navOpacity = 0;

      // Calculate shadow opacity
      const shadowOpacity = navOpacity * 0.4;

      setScrollValues({
        smallLogoPadding: smallPadding,
        navOpacity,
        shadowOpacity: scrollY > 1 ? shadowOpacity : 0,
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll(); // Initial call

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close menu when clicking overlay and prevent body scroll
  useEffect(() => {
    if (isMenuOpen) {
      document.body.classList.add('menu-open');
    } else {
      document.body.classList.remove('menu-open');
    }
    
    return () => {
      document.body.classList.remove('menu-open');
    };
  }, [isMenuOpen]);

  // Calculate background: transitions from white to gradient
  // Gradient colors: #87b3e2 (0%), #0e1c29 (50%), #000004 (100%)
  const bgOpacity = scrollValues.navOpacity;

  // Interpolate gradient colors with white based on opacity
  // Start color: #87b3e2 (135, 179, 226)
  // Mid color: #0e1c29 (14, 28, 41)
  // End color: #000004 (0, 0, 4) - darker right side
  const startR = Math.round(255 * (1 - bgOpacity) + 135 * bgOpacity);
  const startG = Math.round(255 * (1 - bgOpacity) + 179 * bgOpacity);
  const startB = Math.round(255 * (1 - bgOpacity) + 226 * bgOpacity);

  const midR = Math.round(255 * (1 - bgOpacity) + 14 * bgOpacity);
  const midG = Math.round(255 * (1 - bgOpacity) + 28 * bgOpacity);
  const midB = Math.round(255 * (1 - bgOpacity) + 41 * bgOpacity);

  const endR = Math.round(255 * (1 - bgOpacity) + 0 * bgOpacity);
  const endG = Math.round(255 * (1 - bgOpacity) + 0 * bgOpacity);
  const endB = Math.round(255 * (1 - bgOpacity) + 4 * bgOpacity);

  const backgroundColor =
    bgOpacity > 0
      ? `linear-gradient(90deg, rgb(${startR}, ${startG}, ${startB}) 0%, rgb(${midR}, ${midG}, ${midB}) 50%, rgb(${endR}, ${endG}, ${endB}) 100%)`
      : "white";

  // Calculate text color: transitions from black to white based on navOpacity
  const textBrightness = Math.round(255 * scrollValues.navOpacity);
  const textColor = `rgb(${textBrightness}, ${textBrightness}, ${textBrightness})`;

  // Keep header white when mobile menu is open - use separate style object
  const navbarStyle = isMenuOpen && isMobile ? {
    background: 'white',
    backgroundColor: 'white',
    backgroundImage: 'none',
    boxShadow: 'none',
    color: '#000',
  } : {
    background: backgroundColor,
    boxShadow:
      scrollValues.shadowOpacity > 0
        ? `0 2px 4px rgba(0, 0, 0, ${scrollValues.shadowOpacity})`
        : "none",
    color: textColor,
  };

  const smallLogoContainerStyle = {
    paddingTop: `${Math.min(scrollValues.smallLogoPadding, 60)}px`,
  };

  const navLinkStyle = {
    color: textColor,
  };

  const socialLinkStyle = {
    color: textColor,
  };

  const menuIconStyle = {
    color: isMenuOpen && isMobile ? '#000' : textColor,
    opacity: 1,
  };

  return (
    <>
      {/* Fixed Navbar */}
      <header
        className={`header-navbar ${isMenuOpen ? "header-expanded" : ""}`}
        style={navbarStyle}
        onClick={(e) => {
          // Close menu when clicking on overlay (but not on nav itself)
          if (isMenuOpen && e.target === e.currentTarget) {
            closeMenu();
          }
        }}
      >
        <div className="navbar-content">
          {/* Small Logo Container - slides up on scroll */}
          <div
            className={`small-logo-container ${scrollValues.smallLogoPadding >= 58 ? 'hidden' : ''}`}
            style={smallLogoContainerStyle}
          >
            <Link
              to="/"
              className="logo-link"
            >
              <img
                src={logoGC}
                className="small-logo"
                alt={t("globalChicLogoAlt")}
              />
            </Link>
          </div>

          {/* Navigation Links - Desktop & Mobile */}
          <nav
            className={`nav ${isMenuOpen ? "nav-open" : ""}`}
            onClick={(e) => {
              // Prevent clicks inside nav from closing the mobile menu
              // Only stop propagation for mobile view
              if (window.innerWidth <= 768) {
                // Allow clicks on links and buttons to work normally
                if (e.target.tagName === 'A' || e.target.tagName === 'BUTTON' || e.target.closest('a') || e.target.closest('button')) {
                  // Let the click proceed normally
                  return;
                }
                // For other clicks, prevent closing the menu
                e.stopPropagation();
              }
            }}
          >
            {/* 1. Главная */}
            <Link
              to="/"
              className="nav-link"
              style={navLinkStyle}
              onClick={closeMenu}
            >
              {t("home")}
            </Link>

            {/* 2. About Us Dropdown (Desktop) / List (Mobile) */}
            <div 
              ref={aboutDropdownRef}
              className="nav-dropdown-wrapper"
            >
              <button
                className="nav-dropdown-toggle"
                onClick={(e) => {
                  // On mobile, don't toggle - dropdowns are always visible
                  if (window.innerWidth > 768) {
                    toggleAboutDropdown(e);
                  } else {
                    // On mobile, just prevent default and stop propagation
                    e.preventDefault();
                    e.stopPropagation();
                  }
                }}
                onMouseEnter={() => {
                  // Keep dropdown open on hover for desktop
                  if (window.innerWidth > 768) {
                    setAboutDropdownOpen(true);
                    setMembershipDropdownOpen(false);
                  }
                }}
                type="button"
              >
                {t("about_us")}
                <span className={`dropdown-arrow ${aboutDropdownOpen ? 'open' : ''}`}>▼</span>
              </button>
              <div 
                className={`nav-dropdown ${aboutDropdownOpen ? 'open' : ''} mobile-always-open`}
                onClick={(e) => e.stopPropagation()}
              >
                <Link
                  to="/aboutUs"
                  className="nav-dropdown-link"
                  onClick={(e) => {
                    closeMenu();
                    closeDropdowns();
                  }}
                >
                  {t("about_us")}
                </Link>
                <Link
                  to="/ourCountries"
                  className="nav-dropdown-link"
                  onClick={(e) => {
                    closeMenu();
                    closeDropdowns();
                  }}
                >
                  {t("our_countries")}
                </Link>
                <Link
                  to="/ourRules"
                  className="nav-dropdown-link"
                  onClick={(e) => {
                    closeMenu();
                    closeDropdowns();
                  }}
                >
                  {t("our_rules")}
                </Link>
              </div>
            </div>

            {/* 3. Become a Member Dropdown (Desktop) / List (Mobile) */}
            <div 
              ref={membershipDropdownRef}
              className="nav-dropdown-wrapper"
            >
              <button
                className="nav-dropdown-toggle"
                onClick={(e) => {
                  // On mobile, don't toggle - dropdowns are always visible
                  if (window.innerWidth > 768) {
                    toggleMembershipDropdown(e);
                  } else {
                    // On mobile, just prevent default and stop propagation
                    e.preventDefault();
                    e.stopPropagation();
                  }
                }}
                onMouseEnter={() => {
                  // Keep dropdown open on hover for desktop
                  if (window.innerWidth > 768) {
                    setMembershipDropdownOpen(true);
                    setAboutDropdownOpen(false);
                  }
                }}
                type="button"
              >
                {t("become_a_member")}
                <span className={`dropdown-arrow ${membershipDropdownOpen ? 'open' : ''}`}>▼</span>
              </button>
              <div 
                className={`nav-dropdown ${membershipDropdownOpen ? 'open' : ''} mobile-always-open`}
                onClick={(e) => e.stopPropagation()}
              >
                <Link
                  to="/membershipApplication"
                  className="nav-dropdown-link"
                  onClick={(e) => {
                    closeMenu();
                    closeDropdowns();
                  }}
                >
                  {t("membership")}
                </Link>
                <Link
                  to="/associatedMembers"
                  className="nav-dropdown-link"
                  onClick={(e) => {
                    closeMenu();
                    closeDropdowns();
                  }}
                >
                  {t("associated_members")}
                </Link>
              </div>
            </div>

            {/* 4. Contact Us */}
            <Link
              to="/contactUs"
              className="nav-link"
              style={navLinkStyle}
              onClick={closeMenu}
            >
              {t("contact_us")}
            </Link>

            {/* 5. Cultural Events */}
            <Link
              to="/cultural-events"
              className="nav-link"
              style={navLinkStyle}
              onClick={closeMenu}
            >
              {t("cultural_events")}
            </Link>

            {/* 6. Loyalty Program */}
            <Link
              to="/loyalty-program"
              className="nav-link"
              style={navLinkStyle}
              onClick={closeMenu}
            >
              {t("loyalty_program")}
            </Link>

            {/* 7. Subscribe */}
            <Link
              to="/subscribe"
              className="nav-link"
              style={navLinkStyle}
              onClick={closeMenu}
            >
              Subscribe
            </Link>
          </nav>

          {/* Right Side - Social Links, Language Switch, Menu Button */}
          <div className="navbar-right">
            <div className="social-links">
              <ul>
                <li>
                  <a
                    href={
                      process.env.REACT_APP_FACEBOOK_URL ||
                      "https://www.facebook.com/share/g/1AFqzn3Q8Q/?mibextid=wwXIfr"
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Visit our Facebook profile"
                    style={socialLinkStyle}
                  >
                    <Facebook size={20} />
                  </a>
                </li>
                <li>
                  <a
                    href={
                      process.env.REACT_APP_INSTAGRAM_URL ||
                      "https://www.instagram.com/globalchicclub/"
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Visit our Instagram profile"
                    style={socialLinkStyle}
                  >
                    <Instagram size={20} />
                  </a>
                </li>
                <li>
                  <a
                    href={
                      process.env.REACT_APP_TELEGRAM_URL ||
                      "https://t.me/globalchicclub"
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Visit our Telegram channel"
                    style={socialLinkStyle}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                    </svg>
                  </a>
                </li>
              </ul>
            </div>

            {/* Hide language switch on profile page (view/create/edit) - English only for editing */}
            {!isProfilePage && <LanguageSwitch />}

            {/* Mobile Menu Toggle */}
            {isMenuOpen ? (
              <button
                className="close-icon"
                style={{ 
                  color: '#000', 
                  opacity: 1, 
                  visibility: 'visible',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '2.5rem',
                  fontWeight: 300,
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  width: '44px',
                  height: '44px',
                  minWidth: '44px',
                  minHeight: '44px',
                  zIndex: 1300,
                  position: 'relative'
                }}
                onClick={closeMenu}
                aria-label="Close menu"
              >
                &times;
              </button>
            ) : (
              <button
                className="menu-icon"
                style={menuIconStyle}
                onClick={toggleMenu}
              >
                <Menu className="hamburger-icon" />
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Big Logo Row - scrolls naturally with page */}
      <div className="big-logo-row">
        <div className="big-logo-container">
          <Link to={isB2BPage ? "/gcb" : "/"}>
            <StarFieldBackground />
            <img
              src={isB2BPage ? B2BLogo : ChicPlainLogo}
              className="big-logo"
              alt={t("globalChicLogoAlt")}
            />
          </Link>
        </div>
      </div>
    </>
  );
}

export default Header;
